import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Tabs } from '@omnitok/ui';
import { LoadingSpinner } from '@/shared/components';
import { localizationService } from '../services/localization.service';
import type { LocalizationArtifactKind, LocalizationRunItem, LocalizationTargetLocale } from '../types/localization.types';
import JsonViewer from './JsonViewer';

const MODEL_KINDS: LocalizationArtifactKind[] = ['comparison', 'localized', 'original'];
const DIAGNOSTIC_KINDS: LocalizationArtifactKind[] = ['rejectedTranslations'];
const INDEXADO_KINDS: LocalizationArtifactKind[] = [
  'indexadoComparison',
  'indexadoLocalized',
  'indexadoOriginal',
];
const LABELS: Record<LocalizationArtifactKind, string> = {
  final: 'Vista final',
  comparison: 'Cambios',
  localized: 'Preview',
  original: 'Original',
  rejectedTranslations: 'Respuestas rechazadas',
  indexadoComparison: 'Cambios Indexado',
  indexadoLocalized: 'Preview Indexado',
  indexadoOriginal: 'Original Indexado',
};

interface LocalizationArtifactViewerDialogProps {
  runId: string;
  item: LocalizationRunItem | null;
  targetLocales: LocalizationTargetLocale[];
  open: boolean;
  onClose: () => void;
  onDownload: (sku: string, targetLocale: string, kind: LocalizationArtifactKind) => Promise<void>;
}

export default function LocalizationArtifactViewerDialog({
  runId,
  item,
  targetLocales,
  open,
  onClose,
  onDownload,
}: LocalizationArtifactViewerDialogProps) {
  const availableLocales = targetLocales.filter((locale) => item?.localizations?.[locale]?.artifacts)
    .concat(item?.artifacts && targetLocales[0] ? [targetLocales[0]] : [])
    .filter((locale, index, locales) => locales.indexOf(locale) === index);
  const finalAvailable = Boolean(item) && targetLocales.length > 0
    && targetLocales.every((locale) => ['preview-generated', 'already-localized', 'applied'].includes(
      item?.localizations?.[locale]?.status ?? (item?.artifacts ? item.status : '')
    ))
    && availableLocales.length > 0;
  const initialTargetLocale = availableLocales[0] ?? targetLocales[0] ?? 'esCO';
  const [targetLocale, setTargetLocale] = useState<LocalizationTargetLocale>(initialTargetLocale);
  const selectedArtifacts = item?.localizations?.[targetLocale]?.artifacts
    ?? (item?.artifacts && targetLocales[0] === targetLocale ? item.artifacts : undefined);
  const kinds = useMemo<LocalizationArtifactKind[]>(() => [
    ...(finalAvailable ? ['final' as const] : []),
    ...MODEL_KINDS,
    ...DIAGNOSTIC_KINDS.filter((candidateKind) =>
      Boolean(selectedArtifacts?.[candidateKind as keyof typeof selectedArtifacts])
    ),
    ...INDEXADO_KINDS.filter((candidateKind) =>
      Boolean(selectedArtifacts?.[candidateKind as keyof typeof selectedArtifacts])
    ),
  ], [finalAvailable, selectedArtifacts]);
  const [kind, setKind] = useState<LocalizationArtifactKind>(finalAvailable ? 'final' : 'comparison');
  const [documents, setDocuments] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setKind(finalAvailable ? 'final' : 'comparison');
    setTargetLocale(initialTargetLocale);
    setDocuments({});
    setError(null);
  }, [finalAvailable, initialTargetLocale, item?.sku]);

  useEffect(() => {
    if (!kinds.includes(kind)) setKind('comparison');
  }, [kind, kinds]);

  useEffect(() => {
    const documentKey = kind === 'final' ? 'final' : `${targetLocale}:${kind}`;
    if (!open || !item || documents[documentKey] !== undefined) return;
    let active = true;
    setLoading(true);
    setError(null);
    void localizationService.getArtifact(runId, item.sku, targetLocale, kind).then((document) => {
      if (active) setDocuments((current) => ({ ...current, [documentKey]: document }));
    }).catch((artifactError: unknown) => {
      if (active) setError(artifactError instanceof Error
        ? artifactError.message
        : 'No fue posible cargar el artefacto.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [documents, item, kind, open, runId, targetLocale]);

  const documentKey = kind === 'final' ? 'final' : `${targetLocale}:${kind}`;

  return (
    <Modal
      className="localization-modal"
      open={open}
      onClose={onClose}
      title={`Artefactos de ${item?.sku ?? ''}`}
      size="full"
      footer={<Button variant="neutral" ghost onClick={onClose}>Cerrar</Button>}
    >
      <div className="localization-dialog-content">
        {kind !== 'final' && availableLocales.length > 1 && (
          <Tabs
            items={availableLocales.map((locale) => ({ id: locale, label: locale }))}
            activeId={targetLocale}
            onChange={(value) => setTargetLocale(value as LocalizationTargetLocale)}
            variant="pills"
            size="sm"
          />
        )}
        <Tabs
          items={kinds.map((candidateKind) => ({ id: candidateKind, label: LABELS[candidateKind] }))}
          activeId={kind}
          onChange={(value) => setKind(value as LocalizationArtifactKind)}
          variant="underline"
          size="sm"
        />
        {error && <Alert variant="error">{error}</Alert>}
        {loading ? (
          <LoadingSpinner message="Cargando artefacto…" />
        ) : documents[documentKey] !== undefined ? (
          <JsonViewer
            value={documents[documentKey]}
            fileName={kind === 'final'
              ? `${item?.sku}.final.json`
              : `${item?.sku}.${targetLocale}.${kind}.json`}
            onDownload={() => {
              if (item) return onDownload(item.sku, targetLocale, kind);
            }}
          />
        ) : null}
      </div>
    </Modal>
  );
}
